import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";

import {
  createSkill,
  deleteSkill,
  deleteSkillFile,
  getSkill,
  getSkillFile,
  isSkillMissing,
  listShareCandidates,
  listSkills,
  readSkillError,
  toggleSkill,
  updateSkill,
  updateSkillShares,
  updateSkillVisibility,
  uploadSkillPackage,
  uploadSkillPath,
  uploadSkillVersion,
} from "@/components/command-skills/skill-api";
import type {
  Paginated,
  SkillDetail,
  SkillListItem,
  SkillScope,
  SkillVisibility,
} from "@/types/command-skill";

type SkillPages = InfiniteData<Paginated<SkillListItem>>;

function mapSkillPages(
  data: SkillPages | undefined,
  mapItem: (item: SkillListItem) => SkillListItem | null,
): SkillPages | undefined {
  if (!data?.pages) return data;
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      items: page.items
        .map((item) => mapItem(item))
        .filter((item): item is SkillListItem => item !== null),
    })),
  };
}

function patchEnabled(queryClient: QueryClient, id: string, enabled: boolean) {
  queryClient.setQueriesData<SkillPages>({ queryKey: ["skills"] }, (data) =>
    mapSkillPages(data, (item) => (item.id === id ? { ...item, enabled } : item)),
  );
  queryClient.setQueryData<SkillDetail>(["skill", id], (current) =>
    current ? { ...current, enabled } : current,
  );
}

export function cacheSkillDetail(queryClient: QueryClient, detail: SkillDetail) {
  queryClient.setQueryData(["skill", detail.id], detail);
  void queryClient.invalidateQueries({ queryKey: ["skills"] });
}

export function invalidateSkillFiles(queryClient: QueryClient, id: string) {
  void queryClient.invalidateQueries({ queryKey: ["skill-file", id] });
}

export function removeSkillFromCaches(queryClient: QueryClient, id: string) {
  queryClient.removeQueries({ queryKey: ["skill", id] });
  queryClient.removeQueries({ queryKey: ["skill-file", id] });
  queryClient.setQueriesData<SkillPages>({ queryKey: ["skills"] }, (data) =>
    mapSkillPages(data, (item) => (item.id === id ? null : item)),
  );
}

function toastSkillError(error: unknown, fallback: string) {
  toast.error(readSkillError(error, fallback).message);
}

export function useSkillPages(options: {
  section?: SkillScope;
  search?: string;
  enabled?: boolean;
}) {
  const section = options.section ?? "all";
  const search = options.search?.trim() ?? "";
  return useInfiniteQuery({
    queryKey: ["skills", { search, section }],
    enabled: options.enabled !== false,
    initialPageParam: 1,
    staleTime: 15_000,
    queryFn: ({ pageParam }) =>
      listSkills({
        section: options.section,
        search: search || undefined,
        page: pageParam,
        limit: 50,
      }),
    getNextPageParam: (lastPage, pages) =>
      pages.length < lastPage.page_count ? pages.length + 1 : undefined,
  });
}

export function flattenSkillPages(data: SkillPages | undefined): SkillListItem[] {
  return data?.pages.flatMap((page) => page.items) ?? [];
}

export function useSkillDetail(id: string | undefined) {
  return useQuery({
    queryKey: ["skill", id],
    queryFn: () => getSkill(id as string),
    enabled: Boolean(id),
  });
}

export function useSkillFile(id: string | undefined, path: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ["skill-file", id, path],
    queryFn: () => getSkillFile(id as string, path as string),
    enabled: Boolean(id && path && enabled),
  });
}

export function useShareCandidates(search: string, enabled: boolean) {
  return useQuery({
    queryKey: ["skill-share-candidates", search],
    queryFn: () => listShareCandidates({ search: search || undefined, page: 1, limit: 20 }),
    enabled,
  });
}

export function useCreateSkill() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createSkill,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["skills"] });
    },
  });
}

export function useUploadSkill() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      file,
      onProgress,
    }: {
      file: File;
      onProgress?: (percent: number) => void;
    }) => uploadSkillPackage(file, onProgress),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["skills"] });
    },
  });
}

export function useUpdateSkill(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { description?: string; instructions?: string }) => updateSkill(id, body),
    onSuccess: (detail) => {
      cacheSkillDetail(queryClient, detail);
    },
    onError: (error) => {
      if (isSkillMissing(error)) {
        removeSkillFromCaches(queryClient, id);
        toast.error("This skill is no longer available");
        return;
      }
      toastSkillError(error, "Could not save this skill");
    },
  });
}

export function useUploadSkillVersion(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      file,
      onProgress,
    }: {
      file: File;
      onProgress?: (percent: number) => void;
    }) => uploadSkillVersion(id, file, onProgress),
    onSuccess: (detail) => {
      cacheSkillDetail(queryClient, detail);
      invalidateSkillFiles(queryClient, id);
    },
  });
}

export function useUploadSkillFile(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      path,
      file,
      onProgress,
    }: {
      path: string;
      file: File;
      onProgress?: (percent: number) => void;
    }) => uploadSkillPath(id, path, file, onProgress),
    onSuccess: (detail) => {
      cacheSkillDetail(queryClient, detail);
      invalidateSkillFiles(queryClient, id);
    },
  });
}

export function useDeleteSkillFile(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (path: string) => deleteSkillFile(id, path),
    onSuccess: (detail) => {
      cacheSkillDetail(queryClient, detail);
      invalidateSkillFiles(queryClient, id);
    },
  });
}

export function useSkillVisibility(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (visibility: SkillVisibility) => updateSkillVisibility(id, visibility),
    onSuccess: (detail) => {
      cacheSkillDetail(queryClient, detail);
    },
  });
}

export function useSkillShares(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userIds: string[]) => updateSkillShares(id, userIds),
    onSuccess: (result) => {
      queryClient.setQueryData<SkillDetail>(["skill", id], (current) =>
        current ? { ...current, shares: result.shares } : current,
      );
    },
  });
}

export function useToggleSkill() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) => toggleSkill(id, enabled),
    onMutate: async ({ id, enabled }) => {
      await queryClient.cancelQueries({ queryKey: ["skills"] });
      await queryClient.cancelQueries({ queryKey: ["skill", id] });
      const previousLists = queryClient.getQueriesData<SkillPages>({ queryKey: ["skills"] });
      const previousDetail = queryClient.getQueryData<SkillDetail>(["skill", id]);
      patchEnabled(queryClient, id, enabled);
      return { previousLists, previousDetail, id };
    },
    onError: (error, _vars, context) => {
      if (context?.previousLists) {
        for (const [key, data] of context.previousLists) {
          queryClient.setQueryData(key, data);
        }
      }
      if (context) {
        queryClient.setQueryData(["skill", context.id], context.previousDetail);
      }
      if (context && isSkillMissing(error)) {
        removeSkillFromCaches(queryClient, context.id);
        toast.error("This skill is no longer available");
        return;
      }
      toastSkillError(error, "Could not update this skill");
    },
    onSuccess: (result) => {
      patchEnabled(queryClient, result.skill_id, result.enabled);
    },
  });
}

export function useDeleteSkill(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => deleteSkill(id),
    onSuccess: () => {
      removeSkillFromCaches(queryClient, id);
      void queryClient.invalidateQueries({ queryKey: ["skills"] });
    },
  });
}
